import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const line = {
  hidden: { y: "105%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.2, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-charcoal">
      <motion.img
        src="/img/hero.webp"
        alt="Close-up beauty campaign portrait with deep wine lipstick and a fine gold earring"
        style={{ y, scale }}
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover object-[70%_center]"
      />
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(37,32,32,0.72)_0%,rgba(37,32,32,0.35)_45%,rgba(37,32,32,0.05)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-[linear-gradient(0deg,rgba(37,32,32,0.5),transparent)]" />
      <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-20 md:px-10 md:pb-28">
        <p className="eyebrow fade-up text-champagne" style={{ animationDelay: "100ms" }}>
          Visual Stories for Beauty &amp; Jewellery Brands
        </p>
        <h1 data-testid="hero-title" className="mt-6 font-serif text-[3.4rem] font-light leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-[7.5rem]">
          {["Built With", "Intention."].map((t, i) => (
            <span key={t} className="block overflow-hidden pb-2">
              <motion.span className={`block ${i === 1 ? "italic" : ""}`} variants={line} initial="hidden" animate="show" custom={i}>
                {t}
              </motion.span>
            </span>
          ))}
        </h1>
        <p className="fade-up mt-8 max-w-md text-base leading-relaxed text-ivory/90 md:text-lg" style={{ animationDelay: "700ms" }}>
          Thoughtfully created images and films that bring beauty, elegance and personality to your brand.
        </p>
        <div className="fade-up mt-10 flex flex-wrap gap-4" style={{ animationDelay: "850ms" }}>
          <Link to="/portfolio" className="btn btn-ivory" data-testid="hero-explore-portfolio-btn">Explore Portfolio</Link>
          <Link to="/contact" className="btn btn-ghost-light" data-testid="hero-collaborate-btn">Let's Collaborate</Link>
        </div>
      </div>
      <div className="absolute bottom-8 right-6 hidden items-center gap-3 text-[0.65rem] uppercase tracking-[0.3em] text-ivory/80 md:right-10 md:flex">
        Scroll <ArrowDown size={14} strokeWidth={1.25} className="animate-bounce" />
      </div>
    </section>
  );
};
